import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ViewColumn } from '@mui/icons-material';
import { UserRoles } from '@bandido/shared/types';
import { CaseListColumnDefinition, CaseListColumnValue, MAX_CASE_LIST_COLUMNS } from '@bandido/shared/types';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { client } from '@frontend/features/common/services';
import { CONFIG } from '@frontend/config';
import { readColumnSelection, columnSelectionKey } from '../utils/columnSelection';

export interface CaseListCustomColumn {
  id: string;
  label: string;
  render: (caseId: number) => ReactNode;
}

export function useCaseListColumns(workflowIds: string[], caseIds: number[], refreshing: boolean, revision: number) {
  const { t } = useTranslation();
  const { myProfile } = useAuth();
  const admin = Boolean(myProfile?.roles?.split(',').includes(UserRoles.ADMINS));
  const workflowId = workflowIds.length === 1 ? workflowIds[0] : undefined;
  const scope = columnSelectionKey(myProfile?.id, CONFIG.AGENCY_ID, workflowId);
  const [applied, setApplied] = useState<{ scope: string; keys: string[] }>();
  const saved = useMemo(() => readColumnSelection(scope), [scope]);
  const keys = applied?.scope === scope ? applied.keys : saved;
  const [editor, setEditor] = useState<{ scope: string; anchor: HTMLElement; keys: string[]; search: string }>();
  useEffect(() => setEditor(undefined), [scope]);
  const [storageError, setStorageError] = useState(false);
  const definitions = useQuery<CaseListColumnDefinition[]>({
    queryKey: ['case-list-column-definitions', myProfile?.id, CONFIG.AGENCY_ID, workflowId],
    enabled: admin && Boolean(workflowId),
    queryFn: async () => (await client.get('case/list-columns', { params: { workflowId } })).data,
    staleTime: 60000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const fields =
    admin && workflowId
      ? (definitions.data || []).filter((field) => keys.includes(field.key)).slice(0, MAX_CASE_LIST_COLUMNS)
      : [];
  const selectedKeys = fields.map((field) => field.key);
  const values = useQuery<CaseListColumnValue[]>({
    queryKey: ['case-list-column-values', myProfile?.id, CONFIG.AGENCY_ID, workflowId, caseIds, selectedKeys, revision],
    enabled:
      admin && Boolean(workflowId) && fields.length > 0 && caseIds.length > 0 && !refreshing && !definitions.isFetching,
    queryFn: async () =>
      (await client.post('case/list-columns/values', { workflowId, caseIds, fields: selectedKeys })).data,
    retry: false,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });
  const byCase = useMemo(
    () => new Map((values.data || []).map((value) => [`${value.caseId}:${value.key}`, value.value])),
    [values.data],
  );
  const columns: CaseListCustomColumn[] = fields.map((field) => ({
    id: `custom:${field.key}`,
    label: field.label,
    render: (caseId) => {
      if (refreshing || definitions.isFetching || values.isLoading || values.isFetching) return <Skeleton width={90} />;
      if (values.isError)
        return (
          <Tooltip title={t('cases.list.columns.loadError')}>
            <span>!</span>
          </Tooltip>
        );
      const value = byCase.get(`${caseId}:${field.key}`);
      if (value === null || value === undefined || value === '') return '—';
      if (field.type === 'boolean') return t(`cases.list.columns.${value === 'true' ? 'yes' : 'no'}`);
      return (
        <Box
          component="span"
          title={value}
          sx={{ display: 'block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {value}
        </Box>
      );
    },
  }));
  const open = Boolean(editor && editor.scope === scope && admin && workflowId);
  const matches = (definitions.data || []).filter((field) =>
    `${field.blockName} ${field.label}`.toLocaleLowerCase().includes((editor?.search || '').toLocaleLowerCase()),
  );
  const control = admin ? (
    <>
      <Tooltip title={t(workflowId ? 'cases.list.columns.title' : 'cases.list.columns.chooseWorkflow')}>
        <span>
          <IconButton
            aria-label={t('cases.list.columns.title')}
            disabled={!workflowId}
            onClick={(event) => {
              setStorageError(false);
              setEditor({ scope, anchor: event.currentTarget, keys: [...selectedKeys], search: '' });
            }}
          >
            <Badge badgeContent={fields.length} color="primary">
              <ViewColumn color={workflowId ? 'primary' : 'disabled'} />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={open ? editor?.anchor : null}
        onClose={() => setEditor(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 340, maxWidth: '90vw' }}>
          <Typography variant="h6">{t('cases.list.columns.title')}</Typography>
          <TextField
            fullWidth
            size="small"
            label={t('cases.list.columns.search')}
            value={editor?.search || ''}
            onChange={(event) => setEditor((current) => current && { ...current, search: event.target.value })}
            sx={{ my: 1, '& input': { fontSize: 16 } }}
          />
          {definitions.isLoading && <Skeleton height={80} />}
          {definitions.isError && (
            <Alert
              severity="error"
              action={<Button onClick={() => definitions.refetch()}>{t('cases.list.columns.retry')}</Button>}
            >
              {t('cases.list.columns.loadError')}
            </Alert>
          )}
          {!definitions.isLoading && !definitions.isError && matches.length === 0 && (
            <Typography>{t('cases.list.columns.empty')}</Typography>
          )}
          <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {matches.map((field, index) => (
              <Box key={field.key}>
                {(index === 0 || matches[index - 1].blockId !== field.blockId) && (
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    {field.blockName}
                  </Typography>
                )}
                <FormControlLabel
                  label={field.label}
                  control={
                    <Checkbox
                      checked={editor?.keys.includes(field.key) || false}
                      disabled={
                        !editor?.keys.includes(field.key) && (editor?.keys.length || 0) >= MAX_CASE_LIST_COLUMNS
                      }
                      onChange={(_, checked) =>
                        setEditor(
                          (current) =>
                            current && {
                              ...current,
                              keys: checked
                                ? [...current.keys, field.key]
                                : current.keys.filter((key) => key !== field.key),
                            },
                        )
                      }
                    />
                  }
                />
              </Box>
            ))}
          </Box>
          <Typography variant="caption">
            {t('cases.list.columns.selected', { count: editor?.keys.length || 0, max: MAX_CASE_LIST_COLUMNS })}
          </Typography>
          {values.isError && (
            <Alert
              severity="error"
              action={<Button onClick={() => values.refetch()}>{t('cases.list.columns.retry')}</Button>}
            >
              {t('cases.list.columns.loadError')}
            </Alert>
          )}
          {storageError && <Alert severity="warning">{t('cases.list.columns.storageError')}</Alert>}
          <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <Button onClick={() => setEditor(undefined)}>{t('cases.list.columns.cancel')}</Button>
            <Button
              variant="contained"
              disabled={definitions.isFetching || definitions.isError}
              onClick={() => {
                const next = (editor?.keys || [])
                  .filter((key) => definitions.data?.some((field) => field.key === key))
                  .slice(0, MAX_CASE_LIST_COLUMNS);
                setApplied({ scope, keys: next });
                try {
                  localStorage.setItem(scope, JSON.stringify(next));
                  setEditor(undefined);
                } catch {
                  setStorageError(true);
                }
              }}
            >
              {t('cases.list.columns.apply')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  ) : null;
  return { columns, control };
}
